import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('zolaid-theme') || 'light';
    } catch (_) {
      return 'light';
    }
  });

  const applyThemeToDocument = useCallback((isDark) => {
    const root = document.documentElement;
    const body = document.body;
    if (isDark) {
      root.classList.add('dark-theme');
      root.classList.remove('light-theme');
      body.classList.add('dark-theme');
      body.classList.remove('light-theme');
    } else {
      root.classList.add('light-theme');
      root.classList.remove('dark-theme');
      body.classList.add('light-theme');
      body.classList.remove('dark-theme');
    }
  }, []);

  const handleThemeChange = useCallback(
    (newTheme) => {
      setTheme(newTheme);
      try {
        localStorage.setItem('zolaid-theme', newTheme);
      } catch (_) {}

      if (newTheme === 'dark') {
        applyThemeToDocument(true);
      } else if (newTheme === 'light') {
        applyThemeToDocument(false);
      } else {
        document.documentElement.classList.remove('dark-theme', 'light-theme');
        document.body.classList.remove('dark-theme', 'light-theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        applyThemeToDocument(prefersDark);
      }
    },
    [applyThemeToDocument]
  );

  // Apply theme on mount (sync stored theme to DOM)
  useEffect(() => {
    handleThemeChange(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount with initial theme from state initializer
  }, []);

  // Listen for system theme changes when theme is set to 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      handleThemeChange('system');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, handleThemeChange]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: handleThemeChange }}>
      {children}
    </ThemeContext.Provider>
  );
};
